import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import Layers from './Layers'
import Highlight from './Highlight'
import Selection from './Selection'
import Measurements from './Measurements'
import { Symbol } from './Symbol'
import { Layer, Rect } from 'modules/shares/types'
import { InspectorState } from 'modules/shares/Inspector/types'
import { scaleFrame } from '@sketch/utils'
import ContextMenu, { useContextMenu } from './ContextMenu'
import { useComponentsTrigger } from 'modules/shares/components/ComponentsStateContext'

const Container = styled.div`
  position: relative;
`

function getSymbolShareId({
  isForeignSymbol = false,
  shareIdentifier,
  foreignSymbolShareId,
}: {
  isForeignSymbol?: boolean
  shareIdentifier: string
  foreignSymbolShareId?: string
}): string {
  if (isForeignSymbol) {
    return foreignSymbolShareId ?? ''
  }

  return shareIdentifier ?? ''
}

export interface ArtboardInspectorProps {
  className?: string
  zoomFactor: number
  inspector: InspectorState
  staticMenu?: boolean
  shareIdentifier: string
  permanentArtboardShortId?: string
  lockCamera?: (newState: boolean) => void
  isCameraMoving: boolean
}

/**
 * Component in charge of orchestrating the different pieces of the inspector,
 * names could be confusing so this is a quick summary about the different
 * parts of the inspector components inside the canvas:
 *
 * - ContextMenu: layer list that appears when right-click
 * - Symbol: symbol selection and info (purple)
 * - Highlight: blue border shown on hover for any selectable layer
 * - Selection: red lines over the selected layer
 * - Measurements: blue lines and distance shown between a selected and a hover layer
 * - Layers: creates the selectable layers and adds the hover and click events
 */
const ArtboardInspector: React.FC<ArtboardInspectorProps> = ({
  zoomFactor: zoomFactorNotPerformanceFriendly,
  inspector,
  staticMenu,
  shareIdentifier,
  permanentArtboardShortId,
  lockCamera,
  isCameraMoving,
  ...props
}) => {
  const {
    selectedLayerId,
    data: inspectorData,
    onHoverLayer,
    onClickLayer,
    symbol: { symbolLayer, symbolData, symbolArtboard, foreignSymbolShareId },
  } = inspector

  // Trigger async components ingestion
  useComponentsTrigger()

  const layers = inspectorData?.layers
  const ref = useRef<HTMLDivElement>(null)

  let highlight = null
  let selection = null
  let symbol = null
  let measurements = null
  let layersParsed = null

  const zoomFactor = usePerformanceFriendlyZoomFactor(
    zoomFactorNotPerformanceFriendly,
    isCameraMoving
  )

  const [trees, contextMenuX, contextMenuY, menuVisible] = useContextMenu({
    layers,
    ref,
    zoom: zoomFactor,
  })

  useEffect(() => {
    if (!lockCamera) {
      return
    }

    if (menuVisible) {
      lockCamera(true)
    } else {
      lockCamera(false)
    }
  }, [lockCamera, menuVisible])

  const {
    selectedLayer,
    hoveredLayer,
    selectedFrameForMeasurements,
    hoveredFrameForMeasurements,
  } = useHoveredAndSelectedLayersData(inspector)

  // Setup highlights (blue border over any selectable layer) when a layer is hovered
  if (hoveredLayer?.frame) {
    highlight = (
      <Highlight
        data-testid="inspector-canvas-highlight"
        frame={scaleFrame(zoomFactor, hoveredLayer.frame)}
      />
    )
  }

  // Setup selection (red solid and dotted lines) and symbol (purple selection)
  // when a layer is selected
  if (selectedLayer?.frame) {
    selection = (
      <Selection
        data-testid="inspector-canvas-selection"
        frame={scaleFrame(zoomFactor, selectedLayer.frame)}
      />
    )

    const symbolShareIdentifier = getSymbolShareId({
      isForeignSymbol: symbolData?.isForeign,
      shareIdentifier,
      foreignSymbolShareId,
    })

    symbol =
      symbolData?.name && symbolLayer?.id ? (
        <Symbol
          data-testid="inspector-canvas-symbol"
          symbolData={symbolData}
          symbolArtboard={symbolArtboard}
          frame={scaleFrame(zoomFactor, symbolLayer.frame)}
          shareIdentifier={symbolShareIdentifier}
          permanentArtboardShortId={permanentArtboardShortId}
        />
      ) : null
  }

  // Setup measurements layers (blue lines and distance numbers), shown
  // between a selected and a hover layer
  if (selectedFrameForMeasurements) {
    measurements = (
      <Measurements
        zoomFactor={zoomFactor}
        selectedFrame={selectedFrameForMeasurements}
        hoveredFrame={hoveredFrameForMeasurements}
        data-testid="inspector-canvas-measurements"
      />
    )
  }

  // Setup hover and click events to any selectable layer
  if (inspectorData) {
    layersParsed = (
      <Layers
        data-testid="inspector-canvas-layers"
        inspector={inspector}
        zoomFactor={zoomFactor}
      />
    )
  }

  // Order of components is important for z-index purposes
  return (
    <Container ref={ref} data-testid="inspector-canvas" {...props}>
      <ContextMenu
        data-testid="inspector-canvas-context-menu"
        root={inspector?.data?.preset}
        trees={trees}
        onHover={onHoverLayer as (layerId: string) => void}
        onSelect={onClickLayer as (layerId: string) => void}
        selectedLayerId={selectedLayerId as string}
        visible={menuVisible}
        contextMenuX={contextMenuX}
        contextMenuY={contextMenuY}
        staticMenu={Boolean(staticMenu)}
      />
      {symbol}
      {highlight}
      {selection}
      {measurements}
      {layersParsed}
    </Container>
  )
}

function useHoveredAndSelectedLayersData(inspector: InspectorState) {
  const { selectedLayerId, data: inspectorData, hoveredLayerId } = inspector
  const layers = inspectorData?.layers

  return useMemo(() => {
    let hoveredLayer: Layer | undefined
    let selectedLayer: Layer | undefined

    if (hoveredLayerId && selectedLayerId !== hoveredLayerId) {
      hoveredLayer = layers?.[hoveredLayerId as string]
    }

    if (selectedLayerId) {
      selectedLayer = layers?.[selectedLayerId as string]
    }

    const layersProps = {
      selectedLayer: selectedLayer,
      hoveredLayer: hoveredLayer,
    }

    const artboardFrame: Rect | undefined = inspector.data && {
      x: 0,
      y: 0,
      width: inspector.data?.frame?.width ?? 0,
      height: inspector.data?.frame?.height ?? 0,
    }
    const hoveredFrameForMeasurements = hoveredLayer?.frame || artboardFrame
    if (
      selectedLayer &&
      selectedLayerId !== hoveredLayerId &&
      hoveredFrameForMeasurements
    ) {
      return {
        ...layersProps,
        selectedFrameForMeasurements: selectedLayer.frame,
        hoveredFrameForMeasurements: hoveredFrameForMeasurements,
      }
    }

    return {
      ...layersProps,
      hoveredFrameForMeasurements,
      selectedFrameForMeasurements: undefined,
    }
  }, [hoveredLayerId, inspector.data, layers, selectedLayerId])
}

/**
 * Lock the zoom factor while the camera is moving to avoid propagating
 * all the changes of zoom which is then used to calculate different things
 * and can be expensive in terms of performance.
 */
function usePerformanceFriendlyZoomFactor(
  zoomFactor: number,
  isCameraMoving: boolean
) {
  const lastZoomFactorValueRef = useRef(zoomFactor)

  return useMemo(() => {
    const lastKnownValue = lastZoomFactorValueRef.current
    if (isCameraMoving) {
      return lastKnownValue
    }

    // Update once the camera is not moving anymore
    lastZoomFactorValueRef.current = zoomFactor

    return zoomFactor
  }, [isCameraMoving, zoomFactor])
}

export default ArtboardInspector
